// src/oura/requests/RequestBrief.tsx
//
// The pieces a client request is drawn with, shared by the found-requests
// step and the request drawer: the brief (why, key message, checklist,
// things to get right), what the client sent (message and files), and the
// paste-or-drop box that takes text, screenshots, photos and PDFs.

import { useRef, useState, type ClipboardEvent, type DragEvent, type ReactNode } from "react";
import {
    CheckIcon,
    FileTextIcon,
    InfoIcon,
    MessageSquareQuoteIcon,
    PaperclipIcon,
    PencilIcon,
    PlusIcon,
    SearchIcon,
    ShieldCheckIcon,
    SparklesIcon,
    UploadIcon,
    UserRoundIcon,
    XIcon,
    type LucideIcon,
} from "lucide-react";
import { TYPE } from "@oura/ui";
import { cn } from "../lib/cn";
import { ACCEPT, filesError, sizeText, toAttachment, type Attachment } from "./files";

/* ------------------------------------------------------------------------- *
 * The brief: why, key message, checklist, things to get right
 * ------------------------------------------------------------------------- */

const KEY_PREFIX = /^key message:\s*/i;
/** The key message out of the notes, starting with a capital. */
function keyOf(notes?: string[]): string | undefined {
    const k = notes?.find((n) => KEY_PREFIX.test(n))?.replace(KEY_PREFIX, "");
    return k ? k.charAt(0).toUpperCase() + k.slice(1) : undefined;
}

/** A note's icon, from what it is about. */
function noteIcon(note: string): LucideIcon {
    if (/\b(google|search|seo|aeo|ai search|rank)/i.test(note)) return SearchIcon;
    if (/\b(document|accurate|evidence|proof|back(ed)? up|verify|check)/i.test(note)) return ShieldCheckIcon;
    if (/\b(claim|answer|respond|reply|address)/i.test(note)) return MessageSquareQuoteIcon;
    return InfoIcon;
}

export function Brief({
    why,
    checklist,
    notes,
    ticked,
    onTick,
    compact,
}: {
    why?: string;
    checklist?: string[];
    notes?: string[];
    ticked?: string[];
    onTick?: (item: string) => void;
    /** In the found-requests step: tighter, nothing to tick yet. */
    compact?: boolean;
}) {
    const key = keyOf(notes);
    const rest = notes?.filter((n) => !KEY_PREFIX.test(n)) ?? [];
    const items = checklist ?? [];
    const done = items.filter((c) => ticked?.includes(c)).length;
    if (!why && !key && items.length === 0 && rest.length === 0) return null;

    return (
        <div className={compact ? "space-y-3" : "space-y-4"}>
            {(why || key) && (
                <div className="space-y-2.5">
                    {why && (
                        <div>
                            <p className={cn(TYPE.lab, "m-0 mb-1 flex items-center gap-1.5")}>
                                <SparklesIcon aria-hidden size={12} className="text-blue-600" />
                                Why they’re asking
                            </p>
                            <p className={cn(TYPE.tb, "m-0 leading-[22px] text-gray-700")}>{why}</p>
                        </div>
                    )}
                    {key && (
                        <div className="rounded-xl border-l-[3px] border-blue-600 bg-blue-50/70 px-3.5 py-2.5">
                            <p className={cn(TYPE.tm, "m-0 font-semibold text-blue-700")}>Key message</p>
                            <p className={cn(TYPE.tb, "m-0 mt-0.5 font-medium leading-[22px] text-gray-900")}>{key}</p>
                        </div>
                    )}
                </div>
            )}

            {items.length > 0 && (
                <div>
                    <div className="mb-1.5 flex items-baseline justify-between gap-3">
                        <p className={cn(TYPE.lab, "m-0")}>Checklist</p>
                        {onTick && (
                            <p className={cn(TYPE.tm, "m-0 tabular-nums text-gray-500")}>
                                {done} of {items.length} done
                            </p>
                        )}
                    </div>
                    {onTick && (
                        <div className="mb-2 h-1 overflow-hidden rounded-full bg-gray-100">
                            <div className="h-full rounded-full bg-emerald-500 transition-[width] duration-300" style={{ width: `${(done / items.length) * 100}%` }} />
                        </div>
                    )}
                    <ul className="m-0 list-plain divide-y divide-gray-100 rounded-xl bg-gray-50/80 p-0 px-3">
                        {items.map((c, i) => {
                            const on = Boolean(ticked?.includes(c));
                            const box = (
                                <span
                                    aria-hidden
                                    className={cn(
                                        "mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full ring-1 ring-inset transition-colors",
                                        on ? "bg-emerald-500 text-white ring-emerald-500" : "bg-white text-transparent ring-gray-300",
                                    )}
                                >
                                    <CheckIcon size={11} strokeWidth={3} />
                                </span>
                            );
                            const label = <span className={cn(TYPE.tb, "leading-[22px]", on ? "text-gray-400 line-through" : "text-gray-900")}>{c}</span>;
                            return (
                                <li key={c} className="py-2">
                                    {onTick ? (
                                        <button type="button" aria-pressed={on} onClick={() => onTick(c)} className="flex w-full items-start gap-2.5 text-left">
                                            {box}
                                            {label}
                                        </button>
                                    ) : (
                                        <div className="flex items-start gap-2.5">
                                            <span aria-hidden className={cn(TYPE.tm, "mt-[3px] w-[18px] shrink-0 text-center font-semibold tabular-nums text-gray-400")}>
                                                {i + 1}
                                            </span>
                                            {label}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {rest.length > 0 && (
                <div>
                    <p className={cn(TYPE.lab, "m-0 mb-1.5")}>To get right</p>
                    <ul className="m-0 list-plain space-y-2 p-0">
                        {rest.map((n) => {
                            const Icon = noteIcon(n);
                            return (
                                <li key={n} className="flex items-start gap-2.5">
                                    <span aria-hidden className="mt-px grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-gray-100 text-gray-600">
                                        <Icon size={13} />
                                    </span>
                                    <span className={cn(TYPE.tb, "leading-[22px] text-gray-700")}>{n}</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------------- *
 * Files
 * ------------------------------------------------------------------------- */

export function FileTiles({ files, onRemove }: { files: Attachment[]; onRemove?: (id: string) => void }) {
    if (files.length === 0) return null;
    return (
        <ul className="m-0 grid list-plain grid-cols-2 gap-2 p-0 sm:grid-cols-3">
            {files.map((f) => (
                <li key={f.id} className="group relative">
                    <a
                        href={f.url}
                        target="_blank"
                        rel="noreferrer"
                        title={`Open ${f.name}`}
                        className="block overflow-hidden rounded-xl bg-white ring-1 ring-black/[0.06] transition-shadow hover:shadow-md hover:ring-blue-200"
                    >
                        {f.kind === "image" ? (
                            <img src={f.url} alt={f.name} className="h-24 w-full bg-gray-50 object-cover object-top" />
                        ) : (
                            <span className="grid h-24 w-full place-items-center bg-gray-50">
                                <span
                                    className={cn(
                                        "grid h-11 w-9 place-items-center rounded-md text-[10px] font-bold uppercase text-white shadow-sm",
                                        f.kind === "pdf" ? "bg-rose-500" : "bg-gray-400",
                                    )}
                                >
                                    {f.kind === "pdf" ? "PDF" : <FileTextIcon size={16} />}
                                </span>
                            </span>
                        )}
                        <span className="block px-2.5 py-1.5">
                            <span className={cn(TYPE.tm, "block truncate font-medium text-gray-900")}>{f.name}</span>
                            {f.size !== null && <span className={cn(TYPE.tm, "block text-gray-500")}>{sizeText(f.size)}</span>}
                        </span>
                    </a>
                    {onRemove && (
                        <button
                            type="button"
                            aria-label={`Remove ${f.name}`}
                            onClick={() => onRemove(f.id)}
                            className="absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-full bg-white/95 text-gray-700 shadow ring-1 ring-black/10 hover:text-gray-900"
                        >
                            <XIcon size={12} />
                        </button>
                    )}
                </li>
            ))}
        </ul>
    );
}

/* ------------------------------------------------------------------------- *
 * What the client sent
 * ------------------------------------------------------------------------- */

export function ClientMessage({
    who,
    via,
    when,
    subject,
    text,
    files,
}: {
    who?: string | null;
    via: string;
    when: string;
    subject?: string;
    text: string;
    files?: Attachment[];
}) {
    const [all, setAll] = useState(false);
    // The sender's name is often unknown on pasted messages: show a person, not an empty circle.
    const name = who?.trim() ?? "";
    const long = text.length > 360 || text.split("\n").length > 7;
    return (
        <div className="rounded-2xl bg-white p-4 ring-1 ring-black/[0.04]">
            <div className="flex items-center gap-2.5">
                <span aria-hidden className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-blue-50 text-[13px] font-semibold text-blue-700">
                    {name ? name.charAt(0).toUpperCase() : <UserRoundIcon size={15} />}
                </span>
                <div className="min-w-0">
                    <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{name || "The client"}</p>
                    <p className={cn(TYPE.tm, "m-0 text-gray-500")}>
                        {via} · {when}
                    </p>
                </div>
            </div>
            {subject && <p className={cn(TYPE.tb, "m-0 mt-3 font-semibold text-gray-900")}>{subject}</p>}
            {text && (
                <>
                    <p className={cn(TYPE.tb, "m-0 mt-2 whitespace-pre-line break-words leading-[22px] text-gray-700", !all && long && "line-clamp-6")}>{text}</p>
                    {long && (
                        <button type="button" aria-expanded={all} onClick={() => setAll(!all)} className={cn(TYPE.tc, "mt-1 font-medium text-blue-600 hover:text-blue-700")}>
                            {all ? "Show less" : "Show the full message"}
                        </button>
                    )}
                </>
            )}
            {files && files.length > 0 && (
                <div className="mt-3">
                    <p className={cn(TYPE.lab, "m-0 mb-1.5 flex items-center gap-1.5")}>
                        <PaperclipIcon aria-hidden size={12} />
                        {files.length === 1 ? "1 file" : `${files.length} files`}
                    </p>
                    <FileTiles files={files} />
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------------- *
 * Paste or drop
 * ------------------------------------------------------------------------- */

export function PasteBox({
    text,
    onText,
    files,
    onFiles,
}: {
    text: string;
    onText: (t: string) => void;
    files: Attachment[];
    onFiles: (f: Attachment[]) => void;
}) {
    const input = useRef<HTMLInputElement>(null);
    const [over, setOver] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const addFiles = (list: FileList | File[]) => {
        const incoming = Array.from(list);
        if (incoming.length === 0) return;
        const problem = filesError(files, incoming);
        setError(problem);
        if (!problem) onFiles([...files, ...incoming.map(toAttachment)]);
    };
    // Files are collected while any text in the same paste still lands in the box.
    const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
        if (e.clipboardData.files.length > 0) addFiles(e.clipboardData.files);
    };
    const onDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setOver(false);
        addFiles(e.dataTransfer.files);
    };

    return (
        <div
            onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={onDrop}
            className={cn(
                "rounded-2xl bg-white p-3 ring-1 transition-[box-shadow,background-color]",
                over ? "bg-blue-50/60 ring-2 ring-blue-500" : "ring-black/[0.06] focus-within:ring-2 focus-within:ring-blue-600",
            )}
        >
            <textarea
                value={text}
                onChange={(e) => onText(e.target.value)}
                onPaste={onPaste}
                rows={7}
                aria-label="What the client sent"
                placeholder="Paste the client’s message or email here. Screenshots paste straight in (Ctrl+V or ⌘V)."
                className={cn(TYPE.tb, "block w-full resize-y border-0 bg-transparent p-1 leading-[22px] text-gray-900 placeholder:text-gray-400 focus:outline-none")}
            />
            {files.length > 0 && (
                <div className="mt-2">
                    <FileTiles
                        files={files}
                        onRemove={(id) => {
                            setError(null);
                            onFiles(files.filter((f) => f.id !== id));
                        }}
                    />
                </div>
            )}
            {error && (
                <p role="alert" className={cn(TYPE.tm, "m-0 mt-2 text-rose-600")}>
                    {error}
                </p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-dashed border-gray-200 pt-2.5">
                <button
                    type="button"
                    onClick={() => input.current?.click()}
                    className={cn(TYPE.tc, "inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 font-medium text-gray-800 hover:bg-gray-200")}
                >
                    <UploadIcon aria-hidden size={13} />
                    Add files
                </button>
                <span className={cn(TYPE.tm, "text-gray-500")}>{over ? "Drop to add" : "Or drop screenshots, photos, PDFs or text files here"}</span>
                <input
                    ref={input}
                    type="file"
                    multiple
                    accept={ACCEPT}
                    className="hidden"
                    onChange={(e) => {
                        if (e.target.files) addFiles(e.target.files);
                        e.target.value = "";
                    }}
                />
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------------- *
 * Editing the brief
 * ------------------------------------------------------------------------- */

/**
 * Trimmed, blank-free and without repeats (ignoring case). Ticks are stored by
 * the checklist line's text, so two identical lines would tick together.
 */
export function uniqueLines(lines: string[]): string[] {
    const seen = new Set<string>();
    return lines
        .map((l) => l.trim())
        .filter((l) => {
            const k = l.toLocaleLowerCase();
            if (!l || seen.has(k)) return false;
            seen.add(k);
            return true;
        });
}

export interface BriefFields {
    title: string;
    where: string;
    why?: string;
    checklist?: string[];
    notes?: string[];
}

const FIELD = cn(
    TYPE.tb,
    "block w-full rounded-lg border-0 bg-gray-50 px-3 py-2 leading-[22px] text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600",
);

/** Every part of what Oura picked up, editable: title, where, why, key message, checklist, things to get right. */
export function BriefEditor({ value, onSave, onCancel }: { value: BriefFields; onSave: (v: BriefFields) => void; onCancel: () => void }) {
    const [title, setTitle] = useState(value.title);
    const [where, setWhere] = useState(value.where);
    const [why, setWhy] = useState(value.why ?? "");
    const [key, setKey] = useState(keyOf(value.notes) ?? "");
    const [items, setItems] = useState<string[]>(value.checklist ?? []);
    const [rest, setRest] = useState<string[]>(value.notes?.filter((n) => !KEY_PREFIX.test(n)) ?? []);

    const save = () => {
        const checklist = uniqueLines(items);
        const notes = [...(key.trim() ? [`Key message: ${key.trim()}`] : []), ...uniqueLines(rest)];
        onSave({
            title: title.trim() || value.title,
            where: where.trim() || value.where,
            why: why.trim() || undefined,
            checklist: checklist.length > 0 ? checklist : undefined,
            notes: notes.length > 0 ? notes : undefined,
        });
    };

    return (
        <div className="space-y-4">
            <Field label="Request">
                <textarea value={title} rows={2} onChange={(e) => setTitle(e.target.value)} className={cn(FIELD, "resize-none font-semibold")} />
            </Field>
            <Field label="Where">
                <input value={where} onChange={(e) => setWhere(e.target.value)} placeholder="Website, Website FAQ, Google listing…" className={FIELD} />
            </Field>
            <Field label="Why they’re asking">
                <textarea value={why} rows={3} onChange={(e) => setWhy(e.target.value)} placeholder="The problem behind the ask" className={cn(FIELD, "resize-y")} />
            </Field>
            <Field label="Key message">
                <textarea value={key} rows={2} onChange={(e) => setKey(e.target.value)} placeholder="The one point the client wants made" className={cn(FIELD, "resize-y")} />
            </Field>
            <ListField label="Checklist" add="Add an item" items={items} onChange={setItems} />
            <ListField label="To get right" add="Add a point" items={rest} onChange={setRest} />
            <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onCancel} className={cn(TYPE.tc, "rounded-full px-3.5 py-1.5 font-medium text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-gray-50")}>
                    Cancel
                </button>
                <button type="button" onClick={save} className={cn(TYPE.tc, "rounded-full bg-blue-600 px-3.5 py-1.5 font-medium text-white hover:bg-blue-700")}>
                    Save changes
                </button>
            </div>
        </div>
    );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
    return (
        <label className="block">
            <span className={cn(TYPE.lab, "mb-1 block")}>{label}</span>
            {children}
        </label>
    );
}

function ListField({ label, add, items, onChange }: { label: string; add: string; items: string[]; onChange: (l: string[]) => void }) {
    return (
        <div>
            <p className={cn(TYPE.lab, "m-0 mb-1")}>{label}</p>
            <div className="space-y-1.5">
                {items.map((it, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                        <textarea
                            value={it}
                            rows={it.length > 70 ? 2 : 1}
                            aria-label={`${label} ${i + 1}`}
                            onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))}
                            className={cn(FIELD, "resize-none")}
                        />
                        <button
                            type="button"
                            aria-label={`Remove ${label.toLowerCase()} ${i + 1}`}
                            onClick={() => onChange(items.filter((_, j) => j !== i))}
                            className="mt-1.5 grid h-7 w-7 shrink-0 place-items-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                        >
                            <XIcon size={14} />
                        </button>
                    </div>
                ))}
            </div>
            <button type="button" onClick={() => onChange([...items, ""])} className={cn(TYPE.tc, "mt-1.5 inline-flex items-center gap-1 font-medium text-blue-600 hover:text-blue-700")}>
                <PlusIcon aria-hidden size={13} />
                {add}
            </button>
        </div>
    );
}

/** The small "Edit" button that opens the editor. */
export function EditButton({ onClick, label = "Edit" }: { onClick: () => void; label?: string }) {
    return (
        <button type="button" onClick={onClick} className={cn(TYPE.tc, "inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium text-blue-600 hover:bg-blue-50")}>
            <PencilIcon aria-hidden size={12} />
            {label}
        </button>
    );
}
